import React, { useState, useEffect } from 'react';
import billingService from '../../services/billingService';

const FacturacionSRI = () => {
    const [config, setConfig] = useState({
        api_url: 'https://koadoc.koadevs.com/api/sri/documents/create_and_process_invoice_complete/',
        api_token: '',
        environment: 'TEST',
        company_id: '',
        default_tax_rate: '15.00',
        auto_invoice_pos: true,
        is_active: true
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [showToken, setShowToken] = useState(false);
    const [notification, setNotification] = useState(null);
    const [testResult, setTestResult] = useState(null);

    useEffect(() => {
        loadConfig();
    }, []);

    const showNotify = (type, message) => {
        setNotification({ type, message });
        setTimeout(() => setNotification(null), 5000);
    };

    const loadConfig = async () => {
        try {
            setLoading(true);
            const data = await billingService.getConfig();
            if (data) {
                setConfig({
                    api_url: data.api_url || 'https://koadoc.koadevs.com/api/sri/documents/create_and_process_invoice_complete/',
                    api_token: data.api_token || '',
                    environment: data.environment || 'TEST',
                    company_id: data.company_id || '',
                    default_tax_rate: data.default_tax_rate || '15.00',
                    auto_invoice_pos: data.auto_invoice_pos !== undefined ? data.auto_invoice_pos : true,
                    is_active: data.is_active !== undefined ? data.is_active : true
                });
            }
        } catch (error) {
            console.error('Error cargando configuración SRI:', error);
            showNotify('error', 'No se pudo cargar la configuración de facturación.');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        if (e) e.preventDefault();
        try {
            setSaving(true);
            const payload = {
                ...config,
                company_id: config.company_id ? parseInt(config.company_id) : null,
                default_tax_rate: parseFloat(config.default_tax_rate)
            };
            const response = await billingService.saveConfig(payload);
            showNotify('success', response.message || 'Configuración guardada correctamente.');
        } catch (error) {
            console.error('Error guardando configuración:', error);
            const errorMsg = error.response?.data?.message || 'Error al guardar la configuración.';
            showNotify('error', errorMsg);
        } finally {
            setSaving(false);
        }
    };

    const handleTestConnection = async () => {
        if (!config.api_token) {
            showNotify('warning', 'Por favor ingresa un Token antes de probar la conexión.');
            return;
        }

        try {
            setTesting(true);
            setTestResult(null);
            const res = await billingService.testConnection({
                api_url: config.api_url,
                api_token: config.api_token
            });
            setTestResult({ success: true, message: res.message });
        } catch (error) {
            console.error('Error en prueba de conexión:', error);
            const msg = error.response?.data?.message || 'Fallo de conexión con el servidor de facturación.';
            setTestResult({ success: false, message: msg });
        } finally {
            setTesting(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </div>
            </div>
        );
    }

    return (
        <div style={{ padding: '2rem', maxWidth: '850px', margin: '0 auto' }}>
            {/* Encabezado */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                <div>
                    <h2 style={{ color: 'var(--primary-color)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem', fontWeight: 700 }}>
                        <i className="bi bi-file-earmark-check-fill" style={{ color: 'var(--primary-color)' }}></i>
                        Facturación Electrónica SRI (KoaDoc API)
                    </h2>
                    <p style={{ margin: '0.3rem 0 0', color: '#8a7d70', fontSize: '0.95rem' }}>
                        Configuración de conexión, ambiente de emisión y parámetros legales del SRI de Ecuador.
                    </p>
                </div>
                <span style={{
                    padding: '0.4rem 0.8rem',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    backgroundColor: config.environment === 'PROD' ? '#f7e5e0' : '#e6efe0',
                    color: config.environment === 'PROD' ? '#96372c' : '#46693f',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem'
                }}>
                    <span style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: config.environment === 'PROD' ? '#96372c' : '#46693f'
                    }}></span>
                    {config.environment === 'PROD' ? 'Ambiente Producción' : 'Ambiente Pruebas'}
                </span>
            </div>

            {/* Notificaciones */}
            {notification && (
                <div style={{
                    padding: '1rem 1.25rem',
                    borderRadius: '10px',
                    marginBottom: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.8rem',
                    backgroundColor: notification.type === 'success' ? '#f2f7ee' : notification.type === 'warning' ? '#fcf7ea' : '#fcf3f0',
                    border: `1px solid ${notification.type === 'success' ? '#d4e4ca' : notification.type === 'warning' ? '#efdca8' : '#f0cfc5'}`,
                    color: notification.type === 'success' ? '#3d5c37' : notification.type === 'warning' ? '#6f4d17' : '#7a3a2a'
                }}>
                    <i className={`bi ${notification.type === 'success' ? 'bi-check-circle-fill' : notification.type === 'warning' ? 'bi-exclamation-triangle-fill' : 'bi-x-circle-fill'}`} style={{ fontSize: '1.2rem' }}></i>
                    <span style={{ fontSize: '0.95rem', fontWeight: 500 }}>{notification.message}</span>
                </div>
            )}

            {/* Formulario Principal de Conexión SRI */}
            <form onSubmit={handleSave} style={{
                backgroundColor: '#ffffff',
                padding: '2rem',
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                border: '1px solid #e6dfd3'
            }}>

                {/* Switch Activo */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingBottom: '1.25rem',
                    marginBottom: '1.5rem',
                    borderBottom: '1px solid #f4efe6'
                }}>
                    <div>
                        <strong style={{ display: 'block', color: '#1f1f1f', fontSize: '1rem' }}>
                            Módulo de Facturación Electrónica Activo
                        </strong>
                        <span style={{ color: '#8a7d70', fontSize: '0.85rem' }}>
                            Habilita o deshabilita la emisión automática y conexión con el SRI.
                        </span>
                    </div>
                    <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '26px', margin: 0, cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={config.is_active}
                            onChange={(e) => setConfig({ ...config, is_active: e.target.checked })}
                            style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span style={{
                            position: 'absolute',
                            cursor: 'pointer',
                            top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: config.is_active ? 'var(--primary-color, #5f8a5e)' : '#ccc',
                            borderRadius: '34px',
                            transition: '0.3s'
                        }}>
                            <span style={{
                                position: 'absolute',
                                content: '""',
                                height: '20px',
                                width: '20px',
                                left: config.is_active ? '26px' : '4px',
                                bottom: '3px',
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                transition: '0.3s'
                            }}></span>
                        </span>
                    </label>
                </div>

                {/* Switch Auto Facturar en POS */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingBottom: '1.25rem',
                    marginBottom: '1.5rem',
                    borderBottom: '1px solid #f4efe6'
                }}>
                    <div>
                        <strong style={{ display: 'block', color: '#1f1f1f', fontSize: '1rem' }}>
                            Facturación Automática en Punto de Venta (POS)
                        </strong>
                        <span style={{ color: '#8a7d70', fontSize: '0.85rem' }}>
                            Genera y envía automáticamente la factura electrónica al SRI tras confirmar el pago en la caja.
                        </span>
                    </div>
                    <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '26px', margin: 0, cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={config.auto_invoice_pos}
                            onChange={(e) => setConfig({ ...config, auto_invoice_pos: e.target.checked })}
                            style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span style={{
                            position: 'absolute',
                            cursor: 'pointer',
                            top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: config.auto_invoice_pos ? 'var(--primary-color, #5f8a5e)' : '#ccc',
                            borderRadius: '34px',
                            transition: '0.3s'
                        }}>
                            <span style={{
                                position: 'absolute',
                                content: '""',
                                height: '20px',
                                width: '20px',
                                left: config.auto_invoice_pos ? '26px' : '4px',
                                bottom: '3px',
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                transition: '0.3s'
                            }}></span>
                        </span>
                    </label>
                </div>

                {/* Token de Autenticación */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, color: '#4a3f36', marginBottom: '0.4rem' }}>
                        Token de API KoaDoc (Bearer / Token Auth)
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <div style={{ position: 'relative', flex: 1 }}>
                            <input
                                type={showToken ? 'text' : 'password'}
                                value={config.api_token}
                                onChange={(e) => setConfig({ ...config, api_token: e.target.value })}
                                placeholder="vsr_... o token generado en KoaDoc"
                                style={{
                                    width: '100%',
                                    padding: '0.75rem 1rem',
                                    borderRadius: '8px',
                                    border: '1px solid #d6ccbc',
                                    fontSize: '0.95rem',
                                    fontFamily: showToken ? 'inherit' : 'monospace',
                                    backgroundColor: '#faf7f1'
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => setShowToken(!showToken)}
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    border: 'none',
                                    background: 'none',
                                    cursor: 'pointer',
                                    color: '#8a7d70'
                                }}
                            >
                                <i className={`bi ${showToken ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                            </button>
                        </div>
                    </div>
                    <span style={{ display: 'block', color: '#8a7d70', fontSize: '0.8rem', marginTop: '0.3rem' }}>
                        Token asignado a su empresa en el panel de KoaDoc Facturación Electrónica.
                    </span>
                </div>

                {/* URL del Endpoint */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, color: '#4a3f36', marginBottom: '0.4rem' }}>
                        URL Endpoint de Emisión
                    </label>
                    <input
                        type="url"
                        value={config.api_url}
                        onChange={(e) => setConfig({ ...config, api_url: e.target.value })}
                        placeholder="https://koadoc.koadevs.com/api/sri/documents/create_and_process_invoice_complete/"
                        style={{
                            width: '100%',
                            padding: '0.75rem 1rem',
                            borderRadius: '8px',
                            border: '1px solid #d6ccbc',
                            fontSize: '0.95rem',
                            backgroundColor: '#faf7f1'
                        }}
                    />
                    <span style={{ display: 'block', color: '#8a7d70', fontSize: '0.8rem', marginTop: '0.3rem' }}>
                        Endpoint completo para firmar, enviar al SRI y autorizar automáticamente en un solo paso.
                    </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                    {/* Ambiente SRI */}
                    <div>
                        <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, color: '#4a3f36', marginBottom: '0.4rem' }}>
                            Ambiente del SRI
                        </label>
                        <select
                            value={config.environment}
                            onChange={(e) => setConfig({ ...config, environment: e.target.value })}
                            style={{
                                width: '100%',
                                padding: '0.75rem 1rem',
                                borderRadius: '8px',
                                border: '1px solid #d6ccbc',
                                fontSize: '0.95rem',
                                backgroundColor: '#faf7f1'
                            }}
                        >
                            <option value="TEST">Pruebas / Certificación (Ambiente 1)</option>
                            <option value="PROD">Producción (Ambiente 2)</option>
                        </select>
                    </div>

                    {/* Tasa de IVA por Defecto */}
                    <div>
                        <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, color: '#4a3f36', marginBottom: '0.4rem' }}>
                            Tarifa IVA por Defecto (%)
                        </label>
                        <select
                            value={config.default_tax_rate}
                            onChange={(e) => setConfig({ ...config, default_tax_rate: e.target.value })}
                            style={{
                                width: '100%',
                                padding: '0.75rem 1rem',
                                borderRadius: '8px',
                                border: '1px solid #d6ccbc',
                                fontSize: '0.95rem',
                                backgroundColor: '#faf7f1'
                            }}
                        >
                            <option value="15.00">15% (Tarifa General Ecuador)</option>
                            <option value="8.00">8% (Tarifa Reducida Feriados / Turismo)</option>
                            <option value="5.00">5% (Materiales de Construcción)</option>
                            <option value="0.00">0% (Exento / No Objeto)</option>
                        </select>
                    </div>
                </div>

                {/* ID de Empresa (Opcional) */}
                <div style={{ marginBottom: '2rem' }}>
                    <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, color: '#4a3f36', marginBottom: '0.4rem' }}>
                        Company ID en KoaDoc (Opcional)
                    </label>
                    <input
                        type="number"
                        value={config.company_id}
                        onChange={(e) => setConfig({ ...config, company_id: e.target.value })}
                        placeholder="Ej: 1 (Opcional si el token ya está vinculado)"
                        style={{
                            width: '100%',
                            padding: '0.75rem 1rem',
                            borderRadius: '8px',
                            border: '1px solid #d6ccbc',
                            fontSize: '0.95rem',
                            backgroundColor: '#faf7f1'
                        }}
                    />
                </div>

                {/* Resultado de la Prueba */}
                {testResult && (
                    <div style={{
                        padding: '1rem 1.25rem',
                        borderRadius: '8px',
                        marginBottom: '1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.8rem',
                        backgroundColor: testResult.success ? '#f2f7ee' : '#fcf3f0',
                        border: `1px solid ${testResult.success ? '#d4e4ca' : '#f0cfc5'}`,
                        color: testResult.success ? '#3d5c37' : '#7a3a2a'
                    }}>
                        <i className={`bi ${testResult.success ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`} style={{ fontSize: '1.2rem' }}></i>
                        <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>{testResult.message}</span>
                    </div>
                )}

                {/* Botones de Acción */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', borderTop: '1px solid #f4efe6', paddingTop: '1.5rem' }}>
                    <button
                        type="button"
                        onClick={handleTestConnection}
                        disabled={testing || saving}
                        style={{
                            padding: '0.75rem 1.25rem',
                            backgroundColor: '#f4efe6',
                            color: '#4a3f36',
                            border: '1px solid #d6ccbc',
                            borderRadius: '8px',
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.2s'
                        }}
                    >
                        {testing ? (
                            <>
                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                Probando Conexión...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-wifi"></i>
                                Probar Conexión
                            </>
                        )}
                    </button>

                    <button
                        type="submit"
                        disabled={saving || testing}
                        style={{
                            padding: '0.75rem 1.75rem',
                            backgroundColor: 'var(--primary-color, #5f8a5e)',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                        }}
                    >
                        {saving ? (
                            <>
                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                Guardando...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-save-fill"></i>
                                Guardar Configuración
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default FacturacionSRI;
